/**
 * A code pane's Files column (RFC 0354).
 *
 * BESIDE THE FILE, ON THE FILE'S BRANCH. The column lists the ref the active
 * tab reads (`code:<ref>:<path>`), so moving to a tab on another branch moves
 * the list with it. The picker overrides that for this column only, until the
 * active tab's ref changes (3a). Nothing here checks anything out.
 *
 * TWO LISTS. Changed is `/changed`: what the ref changed against the base, as
 * a tree with joined folder chains and line counts (3b). All files is the
 * lazy `/list` tree, one directory per open folder, opened down to the active
 * file so you land where you are (3c). Go to file asks `/files` once per ref
 * and filters locally (3d).
 *
 * A click opens the file as a tab in THIS pane, at the ref listed.
 */

import {
  ChevronDown,
  ChevronRight,
  GitCompareArrows,
  ListTree,
  RotateCw,
  Search,
} from 'lucide-react'
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'
import { fetchVcsFromServer } from '../../sdk/vcs'
import { codeFileId, parseCodeId } from '../../services/code/codeFile'
import {
  PROJECT_FOLDER_EVENT,
  readProjectFolder,
} from '../../services/code/projectFolder'
import {
  type CodeWorktree,
  listProjectWorktrees,
} from '../../services/code/worktrees'
import {
  type CodeBranch,
  type CodeEntry,
  type CodeListing,
  CodeRefSelect,
} from './CodeTreeSection'
import {
  ancestors,
  type ChangedList,
  type ChangedNode,
  changedTree,
  type FilesMode,
  listedRef,
  type RefPick,
  searchPaths,
  tabRef,
} from './codeFiles'
import { FileBadge } from './FileBadge'

/** The git reads the column makes, injectable so a test can count them. */
export interface CodeFilesSource {
  changed: (ref: string) => Promise<ChangedList>
  list: (dir: string, ref: string) => Promise<CodeListing>
  files: (ref: string) => Promise<{ paths: string[]; truncated?: boolean }>
  branches: () => Promise<CodeBranch[]>
  worktrees: () => Promise<CodeWorktree[]>
}

export function serverFilesSource(
  projectId: string,
  sourceId?: string,
): CodeFilesSource {
  return {
    changed: async (ref) =>
      (await fetchVcsFromServer(
        'changed',
        [ref],
        projectId,
        sourceId,
      )) as ChangedList,
    list: async (dir, ref) =>
      (await fetchVcsFromServer(
        'list',
        [dir, ref],
        projectId,
        sourceId,
      )) as CodeListing,
    files: async (ref) =>
      (await fetchVcsFromServer('files', [ref], projectId, sourceId)) as {
        paths: string[]
        truncated?: boolean
      },
    branches: async () =>
      (await fetchVcsFromServer(
        'branches',
        [],
        projectId,
        sourceId,
      )) as CodeBranch[],
    worktrees: () => listProjectWorktrees(projectId, sourceId),
  }
}

type Load<T> =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; value: T }

const message = (e: unknown) => (e instanceof Error ? e.message : String(e))

/** Open, mode and width, per device: a reading preference, not a layout (3e). */
const PREFS_KEY = 'mod_code_files_v1'
export const FILES_DEFAULT_WIDTH = 248
const FILES_MIN = 180
const FILES_MAX = 480

export interface CodeFilesPrefs {
  open: boolean
  mode: FilesMode
  width: number
}

const clampWidth = (px: number) =>
  Number.isFinite(px)
    ? Math.round(Math.min(FILES_MAX, Math.max(FILES_MIN, px)))
    : FILES_DEFAULT_WIDTH

export function readFilesPrefs(): CodeFilesPrefs {
  try {
    const raw = JSON.parse(localStorage.getItem(PREFS_KEY) ?? '{}') as Partial<
      Record<keyof CodeFilesPrefs, unknown>
    >
    return {
      open: raw.open === true,
      mode: raw.mode === 'all' ? 'all' : 'changed',
      width: clampWidth(Number(raw.width ?? FILES_DEFAULT_WIDTH)),
    }
  } catch {
    return { open: false, mode: 'changed', width: FILES_DEFAULT_WIDTH }
  }
}

/**
 * ONE copy for the window, not one per pane: every code pane reads the same
 * prefs, so resizing in one pane cannot write back a mode another pane just
 * changed, and the toggle means the same thing everywhere.
 */
let shared: CodeFilesPrefs | null = null
const listeners = new Set<() => void>()

function currentPrefs(): CodeFilesPrefs {
  shared ??= readFilesPrefs()
  return shared
}

function subscribePrefs(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function setFilesPrefs(patch: Partial<CodeFilesPrefs>): void {
  shared = { ...currentPrefs(), ...patch }
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(shared))
  } catch {
    // No storage: the column forgets on reload, nothing worse.
  }
  for (const listener of listeners) listener()
}

/** Test seam: read storage again on next use. */
export function resetFilesPrefs(): void {
  shared = null
}

/** The prefs, remembered on every change and shared by every pane. */
export function useFilesPrefs(): [
  CodeFilesPrefs,
  (patch: Partial<CodeFilesPrefs>) => void,
] {
  const prefs = useSyncExternalStore(subscribePrefs, currentPrefs)
  return [prefs, setFilesPrefs]
}

export function CodeFilesColumn({
  ...props
}: {
  projectId: string
  activeId: string
  mode: FilesMode
  onMode: (mode: FilesMode) => void
  width: number
  onWidth: (px: number) => void
  onOpen: (docId: string) => void
  source?: CodeFilesSource
}) {
  const sourceId = props.activeId.startsWith('code-source:')
    ? parseCodeId(props.activeId)?.sourceId
    : undefined
  // Each source owns its listings, picker state and pending reads. Remounting
  // prevents a late response from the previous source entering this column.
  return (
    <SourceCodeFilesColumn
      key={`${props.projectId}\0${sourceId ?? 'default'}`}
      {...props}
      sourceId={sourceId}
    />
  )
}

function SourceCodeFilesColumn({
  projectId,
  activeId,
  mode,
  onMode,
  width,
  onWidth,
  onOpen,
  source: sourceOverride,
  sourceId,
}: {
  projectId: string
  /** The active tab's code id. */
  activeId: string
  mode: FilesMode
  onMode: (mode: FilesMode) => void
  width: number
  onWidth: (px: number) => void
  /** Open this code id as a tab in the pane. */
  onOpen: (docId: string) => void
  source?: CodeFilesSource
  sourceId?: string
}) {
  const source = useMemo(
    () => sourceOverride ?? serverFilesSource(projectId, sourceId),
    [sourceOverride, projectId, sourceId],
  )
  const [plainFolder, setPlainFolder] = useState(false)
  useEffect(() => {
    if (sourceOverride) return
    let live = true
    const read = () => {
      void readProjectFolder(projectId, sourceId).then((mount) => {
        if (live) setPlainFolder(!!mount?.path && !mount.git)
      })
    }
    read()
    window.addEventListener(PROJECT_FOLDER_EVENT, read)
    return () => {
      live = false
      window.removeEventListener(PROJECT_FOLDER_EVENT, read)
    }
  }, [projectId, sourceId, sourceOverride])
  const shownMode = plainFolder ? 'all' : mode
  const active = tabRef(activeId) ?? ''
  const [pick, setPick] = useState<RefPick | null>(null)
  const ref = listedRef(active, pick)
  // The pick is dropped, not just ignored, once its tab ref is left: coming
  // back to that branch later follows the tab again. Keyed on `active` alone,
  // so a pick made for a tab that is about to open survives until it does.
  useEffect(() => {
    setPick((p) => (p && p.under !== active ? null : p))
  }, [active])

  /** Open from this column, keeping a branch-picker override for the tab. */
  const open = useCallback(
    (docId: string) => {
      const next = tabRef(docId) ?? ''
      if (next !== ref) setPick({ ref, under: next })
      onOpen(docId)
    },
    [ref, onOpen],
  )

  // Bumped by refresh: every read below is keyed on it.
  const [round, setRound] = useState(0)

  const [changed, setChanged] = useState<Load<ChangedList>>({
    status: 'loading',
  })
  // biome-ignore lint/correctness/useExhaustiveDependencies: `round` re-asks on refresh.
  useEffect(() => {
    if (shownMode !== 'changed') return
    let live = true
    setChanged({ status: 'loading' })
    source.changed(ref).then(
      (value) => live && setChanged({ status: 'ready', value }),
      (e: unknown) =>
        live && setChanged({ status: 'error', message: message(e) }),
    )
    return () => {
      live = false
    }
  }, [shownMode, ref, source, round])

  // Picker data, once, when the column first mounts.
  const [refs, setRefs] = useState<CodeBranch[] | null>(null)
  const [trees, setTrees] = useState<CodeWorktree[] | null>(null)
  useEffect(() => {
    let live = true
    source.branches().then(
      (value) => live && setRefs(value),
      () => live && setRefs([]),
    )
    source.worktrees().then(
      (value) => live && setTrees(value),
      () => live && setTrees([]),
    )
    return () => {
      live = false
    }
  }, [source])

  const [query, setQuery] = useState('')

  const choose = (next: string | null) => {
    const r = next ?? ''
    setPick(r === active ? null : { ref: r, under: active })
  }

  const header = (
    <div className="flex shrink-0 flex-col gap-1.5 border-border/60 border-b px-2 pt-2 pb-2">
      <div className="flex items-center gap-1">
        <div className="flex rounded-md bg-muted/60 p-0.5">
          {!plainFolder && (
            <ModeButton
              selected={shownMode === 'changed'}
              onClick={() => onMode('changed')}
              icon={<GitCompareArrows size={12} />}
              label={
                mode === 'changed' && changed.status === 'ready'
                  ? `${changed.value.files.length}${changed.value.truncated ? '+' : ''} changed`
                  : 'Changed'
              }
            />
          )}
          <ModeButton
            selected={shownMode === 'all'}
            onClick={() => onMode('all')}
            icon={<ListTree size={12} />}
            label="All files"
          />
        </div>
        <span className="flex-1" />
        <button
          type="button"
          aria-label="Refresh files"
          title="List again"
          onClick={() => setRound((n) => n + 1)}
          className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <RotateCw size={11} />
        </button>
      </div>
      {!plainFolder && (
        <CodeRefSelect
          value={ref || null}
          onChange={choose}
          refs={refs}
          trees={trees}
          className="w-full truncate rounded-md border border-border/60 bg-background px-1.5 py-1 text-[11px] text-muted-foreground"
        />
      )}
      <label className="flex items-center gap-1.5 rounded-md border border-border/60 bg-background px-1.5 py-1 text-muted-foreground focus-within:border-foreground/30">
        <Search size={11} className="shrink-0" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setQuery('')
          }}
          placeholder={
            shownMode === 'changed' ? 'Filter changed files' : 'Go to file'
          }
          aria-label={
            shownMode === 'changed' ? 'Filter changed files' : 'Go to file'
          }
          className="min-w-0 flex-1 bg-transparent text-[11px] text-foreground outline-none placeholder:text-muted-foreground/60"
        />
      </label>
    </div>
  )

  return (
    <div
      className="relative flex h-full shrink-0 flex-col border-border/60 border-r"
      style={{ width }}
      data-testid="code-files-column"
    >
      <WidthHandle width={width} onWidth={onWidth} />
      {header}
      <div className="min-h-0 flex-1 overflow-y-auto py-1">
        {shownMode === 'changed' ? (
          <ChangedFiles
            sourceId={sourceId}
            state={changed}
            listRef={ref}
            activeId={activeId}
            query={query}
            onOpen={open}
          />
        ) : query.trim() ? (
          <GoToFile
            sourceId={sourceId}
            source={source}
            listRef={ref}
            round={round}
            query={query}
            activeId={activeId}
            onOpen={open}
          />
        ) : (
          <AllFiles
            sourceId={sourceId}
            source={source}
            listRef={ref}
            round={round}
            activeId={activeId}
            onOpen={open}
          />
        )}
      </div>
    </div>
  )
}

function ModeButton({
  selected,
  onClick,
  icon,
  label,
}: {
  selected: boolean
  onClick: () => void
  icon: ReactNode
  label: string
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={`flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] ${
        selected
          ? 'bg-background text-foreground shadow-sm'
          : 'text-muted-foreground hover:text-foreground'
      }`}
    >
      {icon}
      {label}
    </button>
  )
}

/** Drag the right edge; dragging right widens. Double-click resets. */
function WidthHandle({
  width,
  onWidth,
}: {
  width: number
  onWidth: (px: number) => void
}) {
  const onDown = (e: React.PointerEvent) => {
    e.preventDefault()
    const startX = e.clientX
    const move = (ev: PointerEvent) =>
      onWidth(clampWidth(width + ev.clientX - startX))
    const up = () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      document.body.style.cursor = ''
    }
    document.body.style.cursor = 'col-resize'
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
  }
  return (
    <button
      type="button"
      aria-label="Resize the files column"
      title="Drag to resize, double-click to reset"
      onPointerDown={onDown}
      onDoubleClick={() => onWidth(FILES_DEFAULT_WIDTH)}
      className="absolute inset-y-0 -right-1 z-10 w-2 cursor-col-resize bg-transparent hover:bg-blue-500/30"
    />
  )
}

/** A row in either list: indent, icon, name, and whatever trails it. */
function FileRow({
  depth,
  icon,
  label,
  title,
  active,
  muted,
  struck,
  trailing,
  onClick,
  rowRef,
}: {
  depth: number
  icon: ReactNode
  label: ReactNode
  title?: string
  active?: boolean
  muted?: boolean
  struck?: boolean
  trailing?: ReactNode
  onClick: () => void
  rowRef?: (el: HTMLButtonElement | null) => void
}) {
  return (
    <button
      ref={rowRef}
      type="button"
      title={title}
      onClick={onClick}
      aria-current={active ? 'true' : undefined}
      className={`flex w-full min-w-0 items-center gap-1.5 rounded py-[3px] pr-2 text-left text-xs ${
        active
          ? 'bg-muted text-foreground'
          : muted
            ? 'text-muted-foreground hover:bg-muted/50'
            : 'text-foreground/90 hover:bg-muted/50'
      }`}
      style={{ paddingLeft: 8 + depth * 12 }}
    >
      <span className="grid w-3.5 shrink-0 place-items-center text-muted-foreground">
        {icon}
      </span>
      <span
        className={`min-w-0 flex-1 truncate ${struck ? 'line-through opacity-60' : ''}`}
      >
        {label}
      </span>
      {trailing}
    </button>
  )
}

function Note({ text }: { text: string }) {
  return (
    <p className="px-3 py-1 text-[11px] text-muted-foreground/70">{text}</p>
  )
}

const STATUS_LETTER = {
  added: 'A',
  modified: 'M',
  deleted: 'D',
  renamed: 'R',
} as const

const STATUS_TONE = {
  added: 'text-green-500',
  modified: 'text-amber-500',
  deleted: 'text-red-500',
  renamed: 'text-blue-500',
} as const

/** Scroll a row into view when it becomes the active one. */
function useRevealRow(active: boolean) {
  const done = useRef(false)
  return useCallback(
    (el: HTMLButtonElement | null) => {
      if (!el || !active) {
        if (!active) done.current = false
        return
      }
      if (done.current) return
      done.current = true
      el.scrollIntoView?.({ block: 'nearest' })
    },
    [active],
  )
}

function ActiveAware({
  active,
  children,
}: {
  active: boolean
  children: (rowRef: (el: HTMLButtonElement | null) => void) => ReactNode
}) {
  const rowRef = useRevealRow(active)
  return <>{children(rowRef)}</>
}

function ChangedFiles({
  sourceId,
  state,
  listRef,
  activeId,
  query,
  onOpen,
}: {
  sourceId?: string
  state: Load<ChangedList>
  listRef: string
  activeId: string
  query: string
  onOpen: (docId: string) => void
}) {
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set())
  const files = state.status === 'ready' ? state.value.files : null
  const q = query.trim()
  const shown = useMemo(() => {
    if (!files) return []
    if (!q) return files
    const keep = new Set(
      searchPaths(
        files.map((f) => f.path),
        q,
        files.length,
      ),
    )
    return files.filter((f) => keep.has(f.path))
  }, [files, q])
  const tree = useMemo(() => changedTree(shown), [shown])

  if (state.status === 'loading') return <Note text="Listing changes…" />
  if (state.status === 'error') {
    const absentWorktree = state.message.includes('no worktree has ')
    const path = parseCodeId(activeId)?.path
    return absentWorktree ? (
      <div>
        <Note text="This source does not have that worktree. Choose a ref above or open the file from its source." />
        {sourceId && sourceId !== 'default' && path && (
          <button
            type="button"
            onClick={() => onOpen(codeFileId(path, listRef || null))}
            className="mx-2 rounded px-1 py-0.5 text-left text-[11px] text-foreground hover:bg-muted"
          >
            Try {path.split('/').at(-1)} in Project files
          </button>
        )}
      </div>
    ) : (
      <Note text={`No git server answered: ${state.message}`} />
    )
  }
  if (state.value.files.length === 0)
    return (
      <Note
        text={
          state.value.base
            ? `Nothing changed against ${state.value.base}.`
            : state.value.from
              ? 'Nothing uncommitted.'
              : 'No base shares history with this ref.'
        }
      />
    )
  if (shown.length === 0) return <Note text="No changed file matches." />

  const base = state.value.base
  const render = (nodes: ChangedNode[], depth: number): ReactNode[] =>
    nodes.flatMap((node) => {
      if (node.kind === 'dir') {
        // A filter opens everything it found; collapsing is for browsing.
        const open = q ? true : !collapsed.has(node.path)
        return [
          <FileRow
            key={`dir:${node.path}`}
            depth={depth}
            muted
            icon={open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            label={node.name}
            title={node.path}
            onClick={() =>
              setCollapsed((prev) => {
                const next = new Set(prev)
                if (!next.delete(node.path)) next.add(node.path)
                return next
              })
            }
          />,
          ...(open ? render(node.children, depth + 1) : []),
        ]
      }
      const f = node.file
      // The file view reads deleted content at `from` and shows removed lines.
      const id = codeFileId(f.path, listRef || null, sourceId)
      const isActive = id === activeId
      return [
        <ActiveAware key={`file:${f.path}`} active={isActive}>
          {(rowRef) => (
            <FileRow
              rowRef={rowRef}
              depth={depth}
              icon={<FileBadge name={node.name} />}
              label={node.name}
              title={
                f.oldPath
                  ? `${f.oldPath} → ${f.path}`
                  : `${f.path}${f.uncommitted ? ' (uncommitted)' : ''}`
              }
              active={isActive}
              struck={f.status === 'deleted'}
              onClick={() => onOpen(id)}
              trailing={
                <span className="flex shrink-0 items-center gap-1.5 font-mono text-[10px] tabular-nums">
                  {f.added > 0 && (
                    <span className="text-green-500">+{f.added}</span>
                  )}
                  {f.removed > 0 && (
                    <span className="text-red-500">-{f.removed}</span>
                  )}
                  <span
                    className={`w-2 text-center ${STATUS_TONE[f.status]}`}
                    title={f.uncommitted ? 'Not committed yet' : f.status}
                  >
                    {f.uncommitted ? '•' : STATUS_LETTER[f.status]}
                  </span>
                </span>
              }
            />
          )}
        </ActiveAware>,
      ]
    })

  return (
    <>
      {/* The server picks the nearest trunk; saying which is what lets a
          reader trust the list. */}
      <Note
        text={
          base
            ? `Against ${base}`
            : 'Uncommitted only: no base shares history with this ref'
        }
      />
      {render(tree, 0)}
      {state.value.truncated && (
        <Note text={`Showing the first ${state.value.files.length} files.`} />
      )}
    </>
  )
}

const dirKey = (ref: string, dir: string) => `${ref}\0${dir}`

function AllFiles({
  sourceId,
  source,
  listRef,
  round,
  activeId,
  onOpen,
}: {
  sourceId?: string
  source: CodeFilesSource
  listRef: string
  round: number
  activeId: string
  onOpen: (docId: string) => void
}) {
  const [dirs, setDirs] = useState<Map<string, Load<CodeEntry[]>>>(
    () => new Map(),
  )
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set())
  const asked = useRef(new Set<string>())

  // A refresh forgets every listing.
  const lastRound = useRef(round)
  if (lastRound.current !== round) {
    lastRound.current = round
    asked.current = new Set()
  }

  const load = useCallback(
    (dir: string) => {
      const key = dirKey(listRef, dir)
      if (asked.current.has(key)) return
      asked.current.add(key)
      setDirs((prev) => new Map(prev).set(key, { status: 'loading' }))
      source.list(dir, listRef).then(
        (listing) =>
          setDirs((prev) =>
            new Map(prev).set(key, { status: 'ready', value: listing.entries }),
          ),
        (e: unknown) =>
          setDirs((prev) =>
            new Map(prev).set(key, { status: 'error', message: message(e) }),
          ),
      )
    },
    [source, listRef],
  )

  // REVEAL (3c): the active file's folders open, once per active file, when it
  // is on the ref listed. Opened by hand afterwards, they stay as left.
  const activePath = useMemo(() => {
    const ref = tabRef(activeId)
    if (ref === null || ref !== listRef) return null
    return parseCodeId(activeId)?.path ?? null
  }, [activeId, listRef])
  useEffect(() => {
    if (!activePath) return
    const above = ancestors(activePath)
    if (above.length === 0) return
    setExpanded((prev) => {
      if (above.every((a) => prev.has(a))) return prev
      return new Set([...prev, ...above])
    })
  }, [activePath])

  // List the root and every open folder not yet asked at this ref.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `round` re-lists what is open on refresh.
  useEffect(() => {
    for (const dir of ['', ...expanded]) load(dir)
  }, [expanded, load, round])

  const toggle = (path: string) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (!next.delete(path)) next.add(path)
      return next
    })

  const render = (dir: string, depth: number): ReactNode[] => {
    const state = dirs.get(dirKey(listRef, dir))
    if (!state || state.status === 'loading')
      return dir === '' ? [<Note key="loading" text="Listing…" />] : []
    if (state.status === 'error')
      return [
        <Note
          key={`error:${dir}`}
          text={
            dir === ''
              ? `No git server answered: ${state.message}`
              : state.message
          }
        />,
      ]
    if (state.value.length === 0 && dir === '')
      return [<Note key="empty" text="No files at this ref." />]
    return state.value.flatMap((entry) => {
      if (entry.kind === 'dir') {
        const open = expanded.has(entry.path)
        return [
          <FileRow
            key={`dir:${entry.path}`}
            depth={depth}
            muted
            icon={open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            label={entry.name}
            title={entry.path}
            onClick={() => toggle(entry.path)}
          />,
          ...(open ? render(entry.path, depth + 1) : []),
        ]
      }
      const id = codeFileId(entry.path, listRef || null, sourceId)
      const isActive = id === activeId
      return [
        <ActiveAware key={`file:${entry.path}`} active={isActive}>
          {(rowRef) => (
            <FileRow
              rowRef={rowRef}
              depth={depth}
              icon={<FileBadge name={entry.name} />}
              label={entry.name}
              title={entry.path}
              active={isActive}
              onClick={() => onOpen(id)}
            />
          )}
        </ActiveAware>,
      ]
    })
  }

  return <>{render('', 0)}</>
}

/** Every path at a ref, kept for the column's life (3d). */
const pathCache = new WeakMap<
  CodeFilesSource,
  Map<string, Promise<{ paths: string[]; truncated?: boolean }>>
>()

function pathsAt(
  source: CodeFilesSource,
  sourceId: string | undefined,
  ref: string,
  round: number,
) {
  let byRef = pathCache.get(source)
  if (!byRef) {
    byRef = new Map()
    pathCache.set(source, byRef)
  }
  const key = `${sourceId ?? 'default'}\0${round}\0${ref}`
  // A refresh makes every older round's lists stale, and each can hold up to
  // 200k paths: drop them rather than keep one per refresh for the window's life.
  for (const old of byRef.keys())
    if (!old.startsWith(`${sourceId ?? 'default'}\0${round}\0`))
      byRef.delete(old)
  let hit = byRef.get(key)
  if (!hit) {
    hit = source.files(ref)
    // A failed read is not remembered: the next keystroke may ask again.
    hit.catch(() => byRef?.delete(key))
    byRef.set(key, hit)
  }
  return hit
}

function GoToFile({
  sourceId,
  source,
  listRef,
  round,
  query,
  activeId,
  onOpen,
}: {
  sourceId?: string
  source: CodeFilesSource
  listRef: string
  round: number
  query: string
  activeId: string
  onOpen: (docId: string) => void
}) {
  const [state, setState] = useState<
    Load<{ paths: string[]; truncated?: boolean }>
  >({
    status: 'loading',
  })
  useEffect(() => {
    let live = true
    // The last ref's matches must not stay clickable at this one.
    setState({ status: 'loading' })
    pathsAt(source, sourceId, listRef, round).then(
      (value) => live && setState({ status: 'ready', value }),
      (e: unknown) =>
        live && setState({ status: 'error', message: message(e) }),
    )
    return () => {
      live = false
    }
  }, [source, sourceId, listRef, round])
  const hits = useMemo(
    () =>
      state.status === 'ready' ? searchPaths(state.value.paths, query) : [],
    [state, query],
  )

  if (state.status === 'loading') return <Note text="Reading every path…" />
  if (state.status === 'error')
    return <Note text={`No git server answered: ${state.message}`} />
  if (hits.length === 0) return <Note text="No file matches." />
  return (
    <>
      {hits.map((path) => {
        const slash = path.lastIndexOf('/')
        const name = path.slice(slash + 1)
        const dir = slash === -1 ? '' : path.slice(0, slash)
        const id = codeFileId(path, listRef || null, sourceId)
        return (
          <FileRow
            key={path}
            depth={0}
            icon={<FileBadge name={name} />}
            title={path}
            active={id === activeId}
            onClick={() => onOpen(id)}
            label={
              <>
                {name}
                {dir && (
                  <span className="ml-1.5 text-[10px] text-muted-foreground/70">
                    {dir}
                  </span>
                )}
              </>
            }
          />
        )
      })}
    </>
  )
}
